<script setup lang="ts">
import DialogCloseIcon from "@/assets/editViewIcons/DialogCloseIcon.vue";
import NamaIcon from "@/components/icons/NamaIcon.vue";
import {useDialogPluginComponent} from "quasar";
import Translator, {ElementTranslationInfo} from "@/utils/common/Translator.ts";
import {computed, ref} from "vue";
import ReferenceWidget from "@/components/EditView/widgets/ReferenceWidget.vue";
import {EditPresenter} from "@/utils/presenters/EditPresenter.ts";
import MetaData from "@/utils/common/MetaData.ts";
import BSCEntities from "@/utils/constants/BSCEntities.ts";
import GenericActions from "@/utils/constants/GenericActions.ts";
import NamaCommon from "@/utils/common/NamaCommon.ts";
import DTOTranslationLine = namaTypes.DTOTranslationLine;

const props = defineProps<{ translations: ElementTranslationInfo[], presenter: () => EditPresenter }>()
const {dialogRef, onDialogCancel} = useDialogPluginComponent()

const includeArabicTranslation = ref(true)
const includeEnglishTranslation = ref(true)
const addEntityType = ref(false)
const useFullFieldId = ref(false)
const includeOnlyFieldsWithArabicOrEnglish = ref(true)
const originalTranslations = props.translations;
const presenter = props.presenter();
const fieldMetaData = MetaData.createReferenceFieldMetaData(GenericActions.INTERNAL_IMPORT_INTO_REFERENCE, BSCEntities.TranslationOverRider)
//TODO:: provide("canShowEditUIElementBtn",false) => inject from WidgetContainer

const displayedTranslations = computed(() => {
  const translations = originalTranslations.filter(t => {
    if (includeOnlyFieldsWithArabicOrEnglish.value) {
      if (!(t.arabicText || t.englishText))
        return false;
    }
    return true;
  }).map(item => {
    const translationLine: DTOTranslationLine = {};
    if (addEntityType.value)
      translationLine.forType = item.forType;
    if (useFullFieldId.value)
      translationLine.lineID = item.id;
    else
      translationLine.lineID = item.finalId;
    if (includeArabicTranslation.value)
      translationLine.arabic = item.arabicText || item.currentArabic;
    if (includeEnglishTranslation.value)
      translationLine.english = item.englishText || item.currentEnglish;
    return translationLine;
  })
  return {translations}
});
const toJson = computed(() => {
  return JSON.stringify(displayedTranslations.value, null, 2)
})

async function saveRecord() {
  NamaCommon.setFieldValue(presenter.record.value!, GenericActions.INTERNAL_IMPORT_INTO_ENTITY_TYPE, BSCEntities.TranslationOverRider);
  NamaCommon.setFieldValue(presenter.record.value!, GenericActions.INTERNAL_IMPORT_JSON, toJson.value);
  await presenter.callGUIAction(GenericActions.IMPORT_INTO_RECORD_INTERNAL)
}
</script>

<template>
  <q-dialog ref="dialogRef" transition-hide="jump-down" transition-show="jump-up">
    <q-card class="tw-relative !tw-max-h-[95vh] tw-flex tw-flex-col tw-min-w-fit tw-w-[450px] dark:tw-bg-namaBackground-deep-dark tw-px-4 tw-py-3 !tw-rounded-xl">
      <div class="tw-flex tw-justify-between tw-items-center">
        <div class="tw-flex tw-items-center tw-gap-2">
          <NamaIcon default-icon="translate" class=""/>
          <!--TODO::-->
          <p v-text="Translator.translateId('exportTranslations')" class="tw-text-lg tw-font-semibold tw-leading-none dark:tw-text-white"/>
        </div>
        <q-avatar square class="tw-size-5  tw-cursor-pointer" @click="onDialogCancel">
          <DialogCloseIcon/>
        </q-avatar>
      </div>
      <q-separator class="tw-my-2 dark:tw-bg-white/10"/>
      <q-card-section class="tw-max-h-[inherit] tw-overflow-y-auto !tw-px-2 !tw-py-0 tw-w-full tw-flex tw-flex-col tw-gap-2 tw-items-center">
        <div class="tw-flex tw-gap-2 tw-flex-col tw-pb-2  tw-rounded-md tw-w-full">
          <q-checkbox dense class="nama-checkbox dark:tw-text-white nama-checkbox-selected"
                      label="Include Only Fields With Arabic Or English Translation"
                      v-model="includeOnlyFieldsWithArabicOrEnglish"/>
          <q-checkbox dense class="nama-checkbox dark:tw-text-white nama-checkbox-selected" label="Include Arabic Translations"
                      v-model="includeArabicTranslation"/>
          <q-checkbox dense class="nama-checkbox dark:tw-text-white nama-checkbox-selected" label="Include English Translations"
                      v-model="includeEnglishTranslation"/>
          <q-checkbox dense class="nama-checkbox dark:tw-text-white nama-checkbox-selected" label="Use Full Field ID"
                      v-model="useFullFieldId"/>
          <q-checkbox dense class="nama-checkbox dark:tw-text-white nama-checkbox-selected" label="Add Entity Type"
                      v-model="addEntityType"/>
        </div>
        <textarea
          spellcheck="false"
          v-text="toJson" dir="ltr" rows="16"
          class="tw-w-full tw-rounded-sm tw-text-[#001350] dark:tw-text-white/70 tw-resize-none tw-bg-namaBackground-dim-light dark:tw-bg-namaBackground-bright-dark focus:tw-ring-0
                  tw-outline-none tw-text-xs focus:!tw-border-stone-300 !tw-border-stone-300 dark:focus:tw-border-neutral-600 tw-border dark:tw-border-neutral-600"/>
        <div class="tw-px-3 tw-text-center">
          <ReferenceWidget :presenter="presenter" :field="{id:GenericActions.INTERNAL_IMPORT_INTO_REFERENCE}" :fieldMetaData="fieldMetaData"/>
          <q-btn
            @click="saveRecord()"
            class="tw-rounded-md tw-w-fit tw-shadow-sm nama-light-shadow tw-mt-2 dark:tw-text-white"
            color="primary" no-caps>
            <div class="tw-flex tw-items-center tw-gap-2">
              <span v-text="Translator.translateId('createTranslationFile')"/>
              <NamaIcon default-icon="nm-translation" class="active-white"/>
            </div>
          </q-btn>
        </div>
      </q-card-section>
    </q-card>
  </q-dialog>
</template>
